<!--
  媒体画廊（图片 + 视频）
  用途：商品详情 / 采购实例详情共用的主图与缩略图切换展示
  用法：
    <MediaGallery :items="mediaList" :alt="title" />
  items 结构：[{ id, type: 'image'|'video', src, poster? }]
  也可传入字符串 URL 数组，组件内部会自动规范化
-->
<template>
  <div class="media-gallery">
    <div class="main-stage">
      <!-- 主区域：按类型切换图片或视频播放器 -->
      <img
        v-if="activeItem && activeItem.type === 'image'"
        class="main-media"
        :src="activeItem.src"
        :alt="alt"
      />
      <video
        v-else-if="activeItem && activeItem.type === 'video'"
        ref="videoRef"
        class="main-media main-video"
        :src="activeItem.src"
        :poster="activeItem.poster || undefined"
        controls
        playsinline
        preload="metadata"
      >
        {{ t('media.videoUnsupported') }}
      </video>
      <div v-else class="main-empty">{{ t('media.empty') }}</div>

      <span v-if="activeItem?.type === 'video'" class="media-badge">{{ t('media.video') }}</span>
    </div>

    <div v-if="normalizedItems.length > 1" class="thumbs">
      <button
        v-for="(item, index) in normalizedItems"
        :key="item.id"
        type="button"
        class="thumb"
        :class="{ active: activeIndex === index }"
        :aria-label="item.type === 'video' ? t('media.videoThumb') : t('media.imageThumb')"
        @click="selectMedia(index)"
      >
        <img
          :src="thumbSrc(item)"
          :alt="`${alt}-${index + 1}`"
        />
        <span v-if="item.type === 'video'" class="thumb-play" aria-hidden="true">▶</span>
      </button>
    </div>
  </div>
</template>

<script setup>
import { computed, nextTick, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { normalizeMediaList } from '@/utils/media'

const props = defineProps({
  /** 媒体列表：字符串 URL 或 { type, src, poster } */
  items: {
    type: Array,
    default: () => []
  },
  /** 无列表时的兜底图片 */
  fallback: {
    type: String,
    default: ''
  },
  /** 无障碍与缩略图 alt 文案 */
  alt: {
    type: String,
    default: ''
  }
})

const { t } = useI18n()
const activeIndex = ref(0)
const videoRef = ref(null)

const normalizedItems = computed(() => normalizeMediaList(props.items, props.fallback))
const activeItem = computed(() => normalizedItems.value[activeIndex.value] || null)

/** 缩略图：视频优先用 poster，否则用 src */
const thumbSrc = (item) => item.poster || item.src

/**
 * 切换媒体
 * 用法：点击缩略图；切换前暂停正在播放的视频
 */
const selectMedia = async (index) => {
  if (videoRef.value && !videoRef.value.paused) {
    videoRef.value.pause()
  }
  activeIndex.value = index
  await nextTick()
}

watch(
  normalizedItems,
  (list) => {
    if (!list.length) {
      activeIndex.value = 0
      return
    }
    if (activeIndex.value >= list.length) {
      activeIndex.value = 0
    }
  },
  { immediate: true }
)
</script>

<style scoped>
.media-gallery {
  width: 100%;
}

.main-stage {
  position: relative;
  width: 100%;
  border-radius: 16px;
  overflow: hidden;
  background: var(--color-bg-muted);
}

.main-media {
  width: 100%;
  aspect-ratio: 1 / 1;
  object-fit: cover;
  display: block;
  background: #111;
}

.main-video {
  object-fit: contain;
  background: #0f0f0f;
}

.main-empty {
  aspect-ratio: 1 / 1;
  display: flex;
  align-items: center;
  justify-content: center;
  color: var(--color-text-secondary);
  font-size: 14px;
}

.media-badge {
  position: absolute;
  top: 12px;
  left: 12px;
  padding: 4px 10px;
  border-radius: 999px;
  background: rgba(210, 7, 22, 0.92);
  color: #fff;
  font-size: 12px;
  font-weight: 600;
}

.thumbs {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 12px;
}

.thumb {
  position: relative;
  width: 72px;
  height: 72px;
  border-radius: 10px;
  overflow: hidden;
  border: 2px solid transparent;
  padding: 0;
  background: transparent;
  cursor: pointer;
}

.thumb.active {
  border-color: var(--color-primary);
}

.thumb img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}

.thumb-play {
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.35);
  color: #fff;
  font-size: 14px;
}

/* 案例详情宽图场景：通过父级 class 覆盖比例 */
.media-gallery.ratio-wide .main-media {
  aspect-ratio: 16 / 9;
}

.media-gallery.ratio-wide .thumb {
  width: 80px;
  height: 56px;
}
</style>
